import { useState, useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";
import { ChevronsUpDown } from "lucide-react";
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWheelScroll } from '@/hooks/useWheelScroll';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { captureError } from '@/lib/sentry';

const DEFAULT_RELATIONSHIP_TYPES = [
  'Referral',
  'Colleague',
  'Assistant',
  'Employee',
  'Vendor',
  'Friends & Family',
];

const connectionSchema = z.object({
  contact_id_2: z.string().min(1, "Connected contact is required"),
  relationship_type: z.string().min(1, "Relationship type is required"),
});

const newContactSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  company_name: z.string().optional(),
  relationship_type: z.string().min(1, "Relationship type is required"),
}).refine((data) => data.first_name || data.last_name, {
  message: "At least one name field is required",
  path: ["first_name"],
});

function formatContactDisplay(c: { first_name?: string; last_name?: string; company_name?: string }) {
  const name = [c.first_name, c.last_name].filter((p) => p && p.trim()).join(' ').trim();
  if (name && c.company_name) return `${name} (${c.company_name})`;
  return name || c.company_name || 'Unnamed contact';
}

interface CreateConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  onSuccess?: () => void;
}

export function CreateConnectionDialog({
  open,
  onOpenChange,
  contactId,
  onSuccess,
}: CreateConnectionDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    first_name: string;
    last_name: string;
    company_name?: string;
  }>>([]);
  const [relationshipSuggestions, setRelationshipSuggestions] = useState<string[]>([]);
  const [relationshipInput, setRelationshipInput] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [relTypeOpen, setRelTypeOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"existing" | "new">("existing");
  const skipSearchRef = useRef(false);
  // Both CommandLists are portalled Popovers inside this modal Dialog: wheel needs this.
  const contactListRef = useWheelScroll();
  const relListRef = useWheelScroll();
  const { currentWorkspace } = useWorkspaces();
  const { user } = useAuth();
  const { toast } = useToast();

  const existingContactForm = useForm<z.infer<typeof connectionSchema>>({
    resolver: zodResolver(connectionSchema),
    defaultValues: {
      relationship_type: '',
    },
  });

  const newContactForm = useForm<z.infer<typeof newContactSchema>>({
    resolver: zodResolver(newContactSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      company_name: '',
      relationship_type: '',
    },
  });

  // Reset all form states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      existingContactForm.reset();
      newContactForm.reset();
      setSearchTerm('');
      setSearchResults([]);
      setRelationshipInput('');
      setSearchOpen(false);
      setRelTypeOpen(false);
      setActiveTab("existing");
      skipSearchRef.current = false;
    }
  }, [open]);

  // Fetch all unique relationship types for the workspace, merging defaults
  // at the front so they always appear first in the documented order.
  const fetchAllRelationshipTypes = async () => {
    if (!currentWorkspace) return;

    try {
      const { data: connectionsData, error: connectionsError } = await supabase
        .from('connections')
        .select('relationship_type')
        .eq('workspace_id', currentWorkspace.id);

      if (connectionsError) {
        console.error('Error fetching connections:', connectionsError);
        captureError(connectionsError as Error, { feature: 'connection-relationship-types' });
      }

      const defaultsLower = new Set(DEFAULT_RELATIONSHIP_TYPES.map((t) => t.toLowerCase()));
      const extras = Array.from(
        new Set(
          (connectionsData || [])
            .map((c) => (c.relationship_type || '').trim())
            .filter((t) => t && !defaultsLower.has(t.toLowerCase()))
        )
      ).sort();

      setRelationshipSuggestions([...DEFAULT_RELATIONSHIP_TYPES, ...extras]);
    } catch (error) {
      console.error('Error fetching relationship types:', error);
      captureError(error as Error, { feature: 'connection-relationship-types' });
      setRelationshipSuggestions([...DEFAULT_RELATIONSHIP_TYPES]);
    }
  };

  useEffect(() => {
    if (open && currentWorkspace) {
      console.log('Dialog opened, fetching relationship types');
      fetchAllRelationshipTypes();
    }
  }, [open, currentWorkspace?.id]);

  // Use the same relevancy-ranked RPC as the global nav search for parity.
  const searchContacts = async (search: string) => {
    const trimmed = search.trim();
    if (!trimmed || !currentWorkspace) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('search_contacts_ranked', {
        search_term: trimmed,
        workspace_id_param: currentWorkspace.id,
        page_size: 15,
        page_number: 1,
      });

      if (error) throw error;

      const filtered = (data || [])
        .filter((c: { id: string }) => c.id !== contactId)
        .map((c: { id: string; first_name: string; last_name: string; company_name?: string }) => ({
          id: c.id,
          first_name: c.first_name,
          last_name: c.last_name,
          company_name: c.company_name,
        }));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching contacts:', error);
      captureError(error as Error, { feature: 'connection-contact-search', searchTerm: trimmed });
      toast({
        title: "Error",
        description: "Failed to search contacts",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    // After a contact is selected we set searchTerm to the display label;
    // skip that resulting effect run so we don't fire a stale query that
    // can surface as a flash "Failed to search contacts" toast.
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchContacts(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm, currentWorkspace?.id]);

  const createConnection = async (contactId2: string, relationshipType: string) => {
    if (!currentWorkspace || !user) return;

    try {
      const { error } = await supabase.from('connections').insert({
        contact_id_1: contactId,
        contact_id_2: contactId2,
        relationship_type: relationshipType.trim(),
        workspace_id: currentWorkspace.id,
        created_by: user.id,
        created_at: new Date().toISOString(),
      });

      if (error) throw error;

      toast({
        description: "Connection created successfully",
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating connection:', error);
      captureError(error as Error, { feature: 'create-connection' });
      toast({
        title: "Error",
        description: "Failed to create connection",
        variant: "destructive",
      });
      throw error;
    }
  };

  const onSubmitExisting = async (data: z.infer<typeof connectionSchema>) => {
    if (!currentWorkspace) return;

    setIsLoading(true);
    try {
      await createConnection(data.contact_id_2, data.relationship_type);
    } catch (error) {
      // Error already handled in createConnection
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitNew = async (data: z.infer<typeof newContactSchema>) => {
    if (!currentWorkspace || !user) return;

    setIsLoading(true);
    try {
      const { data: newContact, error: contactError } = await supabase
        .from('contacts')
        .insert({
          workspace_id: currentWorkspace.id,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
          is_company: false,
          created_by: user.id,
        })
        .select()
        .single();

      if (contactError) throw contactError;

      await createConnection(newContact.id, data.relationship_type);
    } catch (error) {
      console.error('Error creating new contact and connection:', error);
      captureError(error as Error, { feature: 'create-connection-with-new-contact' });
      toast({
        title: "Error",
        description: "Failed to create new contact and connection",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderRelationshipField = (formField: any) => (
    <FormItem>
      <FormLabel>Relationship Type</FormLabel>
      <FormControl>
        <Popover open={relTypeOpen} onOpenChange={setRelTypeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={relTypeOpen}
              className="w-full justify-between font-normal"
              type="button"
            >
              <span className="truncate">
                {relationshipInput || "Enter or select relationship type"}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 w-[var(--radix-popover-trigger-width)]"
            align="start"
          >
            <Command>
              <CommandInput
                placeholder="Type or pick a relationship type..."
                value={relationshipInput}
                onValueChange={(v) => {
                  setRelationshipInput(v);
                  formField.onChange(v);
                }}
              />
              <CommandList
                ref={relListRef}
                className="max-h-60 custom-scrollbar"
              >
                <CommandEmpty>No matching suggestion. Press Enter to use what you typed.</CommandEmpty>
                {relationshipSuggestions
                  .filter((type) =>
                    !relationshipInput.trim() ||
                    type.toLowerCase().includes(relationshipInput.toLowerCase())
                  )
                  .map((type) => (
                    <CommandItem
                      key={type}
                      value={type}
                      onSelect={() => {
                        setRelationshipInput(type);
                        formField.onChange(type);
                        setRelTypeOpen(false);
                      }}
                    >
                      {type}
                    </CommandItem>
                  ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FormControl>
      <FormMessage />
    </FormItem>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Connection</DialogTitle>
          <DialogDescription>
            Connect this contact with another contact in your workspace
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "existing" | "new")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing">Select Existing</TabsTrigger>
            <TabsTrigger value="new">Create New</TabsTrigger>
          </TabsList>

          <TabsContent value="existing">
            <Form {...existingContactForm}>
              <form onSubmit={existingContactForm.handleSubmit(onSubmitExisting)} className="space-y-4">
                <FormField
                  control={existingContactForm.control}
                  name="contact_id_2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Contact</FormLabel>
                      <FormControl>
                        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={searchOpen}
                              className="w-full justify-between font-normal"
                              type="button"
                            >
                              <span className="truncate">
                                {searchTerm || "Search contacts…"}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="p-0 w-[var(--radix-popover-trigger-width)]"
                            align="start"
                          >
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder="Search contacts..."
                                value={searchTerm}
                                onValueChange={setSearchTerm}
                              />
                              <CommandList
                                ref={contactListRef}
                                className="max-h-60 custom-scrollbar"
                              >
                                {searchResults.length === 0 ? (
                                  <CommandEmpty>
                                    {searchTerm.trim().length === 0 ? "Type to search…" : "No matches"}
                                  </CommandEmpty>
                                ) : (
                                  searchResults.map((contact) => (
                                    <CommandItem
                                      key={contact.id}
                                      value={contact.id}
                                      onSelect={() => {
                                        field.onChange(contact.id);
                                        skipSearchRef.current = true;
                                        setSearchTerm(formatContactDisplay(contact));
                                        setSearchResults([]);
                                        setSearchOpen(false);
                                      }}
                                    >
                                      <span>{formatContactDisplay(contact)}</span>
                                    </CommandItem>
                                  ))
                                )}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={existingContactForm.control}
                  name="relationship_type"
                  render={({ field }) => renderRelationshipField(field)}
                />

                <DialogFooter>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? "Creating..." : "Create Connection"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="new">
            <Form {...newContactForm}>
              <form onSubmit={newContactForm.handleSubmit(onSubmitNew)} className="space-y-4">
                <FormField
                  control={newContactForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="First name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={newContactForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={newContactForm.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Company name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={newContactForm.control}
                  name="relationship_type"
                  render={({ field }) => renderRelationshipField(field)}
                />

                <DialogFooter>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? "Creating..." : "Create Contact & Connect"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}